var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { getCenter } from "ol/extent";
import { Switchboard } from "../../../events";
import { AsyncRefresh, Observable, Refresh, RefreshProp } from "../../../misc/decorators";
import { Maybe, None } from "../../../misc/option";
import { FeatureQueryManager } from "../requests";
import { Circle, Stroke, Style } from "ol/style";
import { Point } from "ol/geom";
let FeatureData = class FeatureData {
    constructor(opts, identifier) {
        this.identifier = identifier;
        this._clicked = None.instance();
        this._hovered = None.instance();
        this._searched = [];
        this.emitter = Switchboard.get_emitter("SELECTION");
        this.parcelQueryUrls = [];
        this.query_manager = new FeatureQueryManager(opts, identifier);
        if (opts.parcel_query_urls && opts.parcel_query_urls.length) {
            this.parcelQueryUrls = opts.parcel_query_urls;
        }
    }
    changed() {
        this.emitter.emit("change", null);
    }
    set projection(val) {
        this.query_manager.projection = val;
    }
    get clicked() {
        return this._clicked.unwrapOr('');
    }
    set clicked(val) {
        if (val === '')
            this._clicked = None.instance();
        this._clicked = Maybe(val);
    }
    get hovered() {
        return this._hovered.unwrapOr('');
    }
    set hovered(val) {
        if (val === '')
            this._hovered = None.instance();
        this._hovered = Maybe(val);
    }
    get features() { return this._searched; }
    get ids() { return this._searched.map(p => this.id_of(p)); }
    _add_features(features) {
        features = features.filter(f => !this.ids.includes(this.id_of(f))).filter(f => !!this.id_of(f));
        this._searched.push(...features);
    }
    id_of(f) {
        let id;
        for (const url of this.parcelQueryUrls) {
            id = f.get(url.idfield);
            if (id) {
                return id;
            }
        }
        return f.get(this.identifier);
    }
    clear() {
        this._searched = [];
        this._hovered = None.instance();
    }
    async select_feature_by_id(id) {
        if (!id)
            return;
        if (this.ids.some(i => i === id)) {
            this.clicked = id;
            return;
        }
        const selected = await this.query_manager.get_feature_by_id(id);
        if (selected.isSomeAnd(x => x.getGeometry() instanceof Point)) {
            selected.unwrap().setStyle(this.get_selected_point_geometry_style);
        }
        if (selected.isNone()) {
            this.clicked = '';
            return;
        }
        selected
            .map(f => this._searched.push(f))
            .map(_ => { this.clicked = id; });
    }
    async get_feature_by_id(id) {
        if (!id)
            return None.instance();
        if (this.ids.includes(id)) {
            let feature = this.features.find(f => this.id_of(f) === id);
            return Maybe(feature);
        }
        return await this.query_manager.get_feature_by_id(id);
    }
    async get_feature_at_point(point) {
        const existing_feature = this.get_feature_at_point_fast(point);
        return await existing_feature.mapOrElse(async () => await (await this.query_manager.get_feature_at_point(point)).filter(f => !!this.id_of(f)), async (f) => Maybe(f));
    }
    get_feature_at_point_fast(point) {
        if (this.parcelQueryUrls.length)
            return None.instance();
        const intersecting = this._searched.filter(f => {
            const maybe_geom = Maybe(f.getGeometry());
            return maybe_geom.map(g => g.intersectsCoordinate(point)).unwrapOr(false);
        });
        if (intersecting.length > 0)
            return Maybe(intersecting[0]);
        return None.instance();
    }
    async get_feature_center_by_id(id) {
        const feature = Maybe(this._searched.find(f => this.id_of(f) === id));
        return feature.mapOr(await this.query_manager.get_feature_by_id(id).catch((reason) => { throw reason; }), f => Maybe(f))
            .flatmap(f => Maybe(f.getGeometry()))
            .map(geo => geo.getExtent())
            .map(extent => getCenter(extent));
    }
    async add_features_by_ids(ids, focused = '') {
        ids = ids.filter(i => !this.ids.includes(i)).filter(i => !!i);
        if (ids.length === 0)
            return;
        let maybe_features = await this.query_manager.get_features_by_ids(ids);
        if (maybe_features.isSomeAnd(x => x.length > 0)) {
            maybe_features.unwrap().forEach(f => {
                if (f.getGeometry() instanceof Point) {
                    if (this.id_of(f) === focused) {
                        f.setStyle(this.get_selected_point_geometry_style);
                    }
                    else {
                        f.setStyle(this.get_searched_point_geometry_style);
                    }
                }
            });
        }
        maybe_features.map(fs => { this._add_features(fs); return fs; });
        if (this.ids.includes(focused) || focused === '')
            this.clicked = focused;
    }
    remove_features_by_ids(ids) {
        ids = ids.filter(i => this.ids.includes(i)).filter(i => !!i);
        if (ids.length === 0)
            return;
        this._searched = this.features.filter(f => !ids.includes(this.id_of(f)));
    }
    async add_feature_at_point(point) {
        const f = await this.get_feature_at_point(point);
        f.map(feat => {
            if (!this._searched.includes(feat)) {
                if (feat.getGeometry() instanceof Point) {
                    feat.setStyle(this.get_selected_point_geometry_style);
                }
                this._add_features([feat]);
                this._clicked = Maybe(this.id_of(feat));
            }
        });
    }
    remove_feature_at_point(coordinate) {
        const feature = this.get_feature_at_point_fast(coordinate);
        feature.map(f => this._searched = this._searched.filter(p => p !== f));
    }
    async add_features_by_geometry(geometry) {
        if (geometry instanceof Point) {
            const f = this.get_feature_at_point_fast(geometry.getCoordinates());
            await f.mapOrElse(async () => await this.add_feature_at_point(geometry.getCoordinates()), async (f) => { this._searched = this._searched.filter(p => p !== f); });
            return;
        }
        else {
            if (this.parcelQueryUrls.length) {
                const features_collection = [];
                for (const url of this.parcelQueryUrls) {
                    const maybe_features = await this.query_manager.get_features_by_geometry(geometry, url.idfield, url.url);
                    maybe_features.map(fs => features_collection.push(fs));
                }
                if (features_collection.length) {
                    const features = features_collection.flat();
                    features.forEach(f => {
                        if (f.getGeometry() instanceof Point) {
                            f.setStyle(this.get_selected_point_geometry_style);
                        }
                    });
                    this.clear();
                    this._add_features(features);
                }
            }
            else {
                let maybe_features = await this.query_manager.get_features_by_geometry(geometry);
                maybe_features.map(fs => { this.clear(); this._add_features(fs); });
            }
        }
    }
    async get_buffer_by_geometry(geometries = [], request_url, options) {
        return await this.query_manager.get_buffer_by_geometry(geometries, request_url, options);
    }
    async get_neighbors_by_geometry(geometries = [], request_url) {
        return await this.query_manager.get_neighbours_by_geometry(geometries, request_url);
    }
    remove_features_by_geometry(geometry) {
        const feature_to_geom = (f) => Maybe(f.getGeometry());
        const geom_to_extent = (g) => g.getExtent();
        this._searched = this._searched.filter(f => {
            return feature_to_geom(f)
                .map(g => geom_to_extent(g))
                .map(e => !geometry.intersectsExtent(e))
                .unwrapOr(false);
        });
    }
    get_selected_point_geometry_style() {
        return new Style({
            image: new Circle({
                radius: 10,
                stroke: new Stroke({ color: 'rgba(255,165,0,1.0)', width: 4 })
            })
        });
    }
    get_searched_point_geometry_style() {
        return new Style({
            image: new Circle({
                radius: 10,
                stroke: new Stroke({ color: 'rgba(0,189,255,0.9)', width: 3 })
            })
        });
    }
};
__decorate([
    RefreshProp
], FeatureData.prototype, "clicked", null);
__decorate([
    RefreshProp
], FeatureData.prototype, "hovered", null);
__decorate([
    Refresh
], FeatureData.prototype, "_add_features", null);
__decorate([
    Refresh
], FeatureData.prototype, "clear", null);
__decorate([
    AsyncRefresh
], FeatureData.prototype, "select_feature_by_id", null);
__decorate([
    AsyncRefresh
], FeatureData.prototype, "add_features_by_ids", null);
__decorate([
    Refresh
], FeatureData.prototype, "remove_features_by_ids", null);
__decorate([
    AsyncRefresh
], FeatureData.prototype, "add_feature_at_point", null);
__decorate([
    Refresh
], FeatureData.prototype, "remove_feature_at_point", null);
__decorate([
    AsyncRefresh
], FeatureData.prototype, "add_features_by_geometry", null);
__decorate([
    Refresh
], FeatureData.prototype, "remove_features_by_geometry", null);
FeatureData = __decorate([
    Observable
], FeatureData);
export { FeatureData };
